import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, Divider, Table, TableHead, TableBody, TableRow, TableCell,
    FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './invoiceTypesApi';
import type { InvoiceType, Language, PrintDocumentTemplate } from './invoiceTypesApi';
import { sx14, paperSx, FormRow, DetailHeader, mergeLocalizations } from '../common/detailView';

interface Props {
    invoiceTypeId: number | null;
}

export function InvoiceTypeView({ invoiceTypeId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<InvoiceType | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [templates, setTemplates] = useState<PrintDocumentTemplate[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<InvoiceType | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [langs, tpls] = await Promise.all([api.getLanguages(), api.getPrintDocumentTemplates()]);
            setLanguages(langs);
            setTemplates(tpls);
            if (invoiceTypeId === null) {
                setItem({
                    id: 0, name: '', printDocumentTemplate: null,
                    localizations: mergeLocalizations([], langs, buildDefaultLoc)
                });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getInvoiceType(invoiceTypeId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [invoiceTypeId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew ? await api.createInvoiceType(item) : await api.updateInvoiceType(item);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof InvoiceType>(field: K, value: InvoiceType[K]) => {
        setItem({ ...item, [field]: value });
    };

    const updateLocalization = (languageId: number, name: string) => {
        update('localizations', item.localizations.map(loc =>
            loc.language.id === languageId ? { ...loc, name } : loc
        ));
    };

    const sortedLocs = [...item.localizations].sort((a, b) =>
        (a.language.name ?? '').localeCompare(b.language.name ?? '')
    );

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={item.name ?? ''}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('invoicetype.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                value={item.name ?? ''}
                                onChange={e => update('name', e.target.value)} />
                        )}
                    </FormRow>

                    <FormRow label={t('invoicetype.printDocumentTemplate')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.printDocumentTemplate?.description}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.printDocumentTemplate?.id ?? ''}
                                    onChange={e => {
                                        const tpl = templates.find(x => x.id === e.target.value);
                                        update('printDocumentTemplate', tpl ?? null);
                                    }}
                                >
                                    {[...templates]
                                        .sort((a, b) => a.id - b.id)
                                        .map(tpl => (
                                            <MenuItem key={tpl.id} value={tpl.id}>{tpl.description}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <Divider />

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('invoicetype.name')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedLocs.map(loc => (
                                <TableRow key={loc.language.id}>
                                    <TableCell sx={{ ...sx14, width: 150 }}>{loc.language.name}</TableCell>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{loc.name}</Typography>
                                        ) : (
                                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                value={loc.name ?? ''}
                                                onChange={e => updateLocalization(loc.language.id, e.target.value)} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
